import React, { useState } from 'react';
import { Button } from 'antd';
import moment from 'moment-timezone';
import type { Doc31AuditEvent, Doc31EditionStatus } from '../../../types/doc31';
import { DOC31_ACTION_LABELS, DOC31_STATUS_LABELS } from '../../../utils/doc31';
import {
    Doc31ChangeRow,
    Doc31FormattedValue,
    doc31ChangeRows,
    doc31CouponCreatedSummary,
} from './format';
import style from './style.module.scss';

interface Props {
    event: Doc31AuditEvent;
    timezone: string;
}

const Value = ({
    value,
    className,
}: {
    value: Doc31FormattedValue;
    className: string;
}) => (
    <span className={className} title={value.title}>
        {value.text}
    </span>
);

const ChangeTable = ({ rows }: { rows: Doc31ChangeRow[] }) => (
    <table className={style.changes}>
        <tbody>
            {rows.map((row) => (
                <tr key={row.path}>
                    <th scope="row" className={style.field}>
                        {row.label}
                    </th>
                    <td className={style.diff}>
                        {!row.isNew && (
                            <>
                                <Value
                                    value={row.before}
                                    className={style.before}
                                />
                                <span className={style.arrow}>→</span>
                            </>
                        )}
                        <Value value={row.after} className={style.after} />
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

export const Doc31HistoryEntry = ({ event, timezone }: Props) => {
    const [expanded, setExpanded] = useState(false);
    const rows = doc31ChangeRows(event, timezone);
    const isCouponCreated = event.action === 'coupon.created';
    const isTransition = event.action === 'edition.transitioned';
    const visibleRows = isTransition
        ? rows.filter((row) => row.path !== 'status')
        : rows;
    const [statusBefore, statusAfter] = [
        event.before?.status,
        event.after?.status,
    ].map((status) =>
        typeof status === 'string'
            ? DOC31_STATUS_LABELS[status as Doc31EditionStatus] ?? status
            : '—'
    );

    return (
        <li className={style.entry}>
            <div className={style.meta}>
                <time className={style.time} dateTime={event.effectiveAt}>
                    {moment.tz(event.effectiveAt, timezone).format('HH:mm')}
                </time>
                <span className={style.action}>
                    {DOC31_ACTION_LABELS[event.action] ?? event.action}
                </span>
                {isTransition && (
                    <span className={style.move}>
                        {`${statusBefore} → ${statusAfter}`}
                    </span>
                )}
                {event.resourceType === 'coupon_promotion' && (
                    <span className={style.resource}>
                        Coupon {event.resourceId}
                    </span>
                )}
                <span className={style.actor}>{event.actorIdentifier}</span>
            </div>
            {event.reason && (
                <blockquote className={style.reason}>{event.reason}</blockquote>
            )}
            {isCouponCreated ? (
                <div className={style.summary}>
                    <span>{doc31CouponCreatedSummary(event)}</span>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        aria-expanded={expanded}
                    >
                        {expanded ? 'Hide fields' : 'Show all fields'}
                    </Button>
                    {expanded && <ChangeTable rows={rows} />}
                </div>
            ) : (
                visibleRows.length > 0 && <ChangeTable rows={visibleRows} />
            )}
        </li>
    );
};
